import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Save, ChevronDown, ChevronRight, Star } from "lucide-react";
import CityAutocomplete from "@/components/CityAutocomplete";
import { evaluateFieldStatus as sharedEvaluate } from "@/lib/screening";
import ScreeningScorecard from "@/components/candidate/ScreeningScorecard";

/**
 * ScreeningProfileEditor — recruiter-only deep-screening capture surfaced on the
 * Overview tab. Talks directly to PATCH /candidates/{cid} with the
 * `screening_profile` partial dict.
 *
 * - Hidden entirely from interviewers (parent controls visibility).
 * - Auto-saves on blur, debounced, never blocks the rest of the page.
 * - Uses Google-Maps-backed CityAutocomplete for the two address fields so
 *   they map to a real location and can later power the geo-map view.
 */
export default function ScreeningProfileEditor({ candidateId, initial, initialMeta, onSaved }) {
    const [fields, setFields] = useState(null);
    const [profile, setProfile] = useState(initial || {});
    const [meta, setMeta] = useState(initialMeta || {});
    const [openBlocks, setOpenBlocks] = useState({});
    const [savingKey, setSavingKey] = useState(null);
    // Per-company screening-field preferences (importance + preferred answers).
    // Loaded from GET /company/screening-prefs; drives yellow/green/red
    // border cues in FieldRow. Missing key ⇒ no cue.
    const [prefs, setPrefs] = useState({});
    const saveTimer = useRef(null);

    useEffect(() => { setProfile(initial || {}); }, [initial, candidateId]);
    useEffect(() => { setMeta(initialMeta || {}); }, [initialMeta, candidateId]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/screening-fields");
                if (!cancelled) setFields(data || { fields: [] });
            } catch (e) {
                if (!cancelled) {
                    // 403 for interviewer is expected — parent should never mount us anyway
                    toast.error(formatApiError(e));
                }
            }
            try {
                const { data } = await api.get("/company/screening-prefs");
                if (!cancelled) setPrefs(data?.screening_prefs || {});
            } catch { /* prefs are optional — silently fall back to empty */ }
        })();
        return () => { cancelled = true; };
    }, []);

    const blocks = useMemo(() => {
        const groups = {};
        (fields?.fields || []).forEach((f) => {
            (groups[f.block] = groups[f.block] || []).push(f);
        });
        return groups;
    }, [fields]);

    // Default-open the first 2 blocks so the recruiter sees what's possible.
    useEffect(() => {
        if (fields && Object.keys(openBlocks).length === 0) {
            const names = Object.keys(blocks);
            const init = {};
            names.forEach((n, i) => { init[n] = i < 2; });
            setOpenBlocks(init);
        }
    }, [fields, blocks, openBlocks]);

    // Listen for scorecard-driven navigation events. When a user clicks a
    // pill in ScreeningScorecard, we open the block that owns the target
    // field, scroll it into view, and flash a subtle ring for 1.5s. Using
    // a DOM CustomEvent avoids threading a callback through 3-4 layers.
    useEffect(() => {
        const handler = (ev) => {
            const key = ev.detail?.key;
            if (!key) return;
            const f = (fields?.fields || []).find((x) => x.key === key);
            if (!f) return;
            setOpenBlocks((o) => ({ ...o, [f.block]: true }));
            // Wait a beat for the block to render, then scroll + flash.
            setTimeout(() => {
                const el = document.querySelector(`[data-testid="sp-row-${key}"]`);
                if (!el) return;
                el.scrollIntoView({ behavior: "smooth", block: "center" });
                el.classList.add("ring-2", "ring-ikb", "ring-offset-2");
                setTimeout(() => el.classList.remove("ring-2", "ring-ikb", "ring-offset-2"), 1500);
            }, 120);
        };
        window.addEventListener("screening:navigate", handler);
        return () => window.removeEventListener("screening:navigate", handler);
    }, [fields]);

    const update = (key, value) => {
        setProfile((p) => ({ ...p, [key]: value }));
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(() => save(key, value), 600);
    };

    /**
     * Save MULTIPLE fields in one PATCH (used by the Compensation block when
     * a user edit triggers cascading auto-calculations — e.g. typing Current
     * CTC Annual auto-fills Current Monthly Gross AND Desired Increment %).
     * One request → one rollup activity event but separate per-field audit
     * entries (handled server-side in /candidates/{cid} PATCH).
     */
    const updateMany = (patch) => {
        setProfile((p) => ({ ...p, ...patch }));
        if (saveTimer.current) clearTimeout(saveTimer.current);
        saveTimer.current = setTimeout(async () => {
            try {
                setSavingKey(Object.keys(patch)[0] || null);
                const body = { screening_profile: { ...patch } };
                // Convert "" → null so the field is properly cleared server-side
                Object.keys(body.screening_profile).forEach((k) => {
                    if (body.screening_profile[k] === "") body.screening_profile[k] = null;
                });
                const { data } = await api.patch(`/candidates/${candidateId}`, body);
                if (data?.screening_profile_meta) setMeta(data.screening_profile_meta);
                onSaved?.(data);
            } catch (e) {
                toast.error(formatApiError(e));
            } finally {
                setSavingKey(null);
            }
        }, 600);
    };

    const save = async (key, value) => {
        try {
            setSavingKey(key);
            const patch = { screening_profile: { [key]: value === "" ? null : value } };
            const { data } = await api.patch(`/candidates/${candidateId}`, patch);
            // Refresh local meta so the "Edited by X on Y" line updates instantly
            if (data?.screening_profile_meta) setMeta(data.screening_profile_meta);
            onSaved?.(data);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSavingKey(null);
        }
    };

    if (!fields) return <div className="text-xs text-muted-foreground">Loading screening fields…</div>;

    return (
        <section className="space-y-3" data-testid="screening-editor">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h3 className="font-display font-bold text-lg text-foreground">Deep screening</h3>
                <div className="flex items-center gap-3 flex-wrap">
                    <ScreeningScorecard
                        profile={profile}
                        prefs={prefs}
                        fields={fields?.fields || []}
                        compact
                    />
                    <span className="text-xs text-muted-foreground">Auto-saves on blur · recruiter-only</span>
                </div>
            </div>
            {Object.entries(blocks).map(([block, list]) => {
                const open = !!openBlocks[block];
                // Count how many "important" fields in this block are still
                // empty — helps recruiters spot blocks that need attention
                // even when collapsed.
                const importantUnfilled = list.filter((f) => {
                    const p = prefs?.[f.key];
                    if (p?.importance !== "important") return false;
                    const v = profile[f.key];
                    return v === undefined || v === null || v === "";
                }).length;
                return (
                    <div key={block} className="border border-border bg-white">
                        <button
                            type="button"
                            onClick={() => setOpenBlocks((o) => ({ ...o, [block]: !o[block] }))}
                            className="w-full flex items-center justify-between px-3 py-2 hover:bg-secondary/40 text-left"
                            data-testid={`screening-block-${block.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                        >
                            <span className="font-display font-bold text-base inline-flex items-center gap-1.5">
                                {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                                {block}
                                {importantUnfilled > 0 && (
                                    <span
                                        className="ml-2 inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-medium bg-amber-100 text-amber-900 border border-amber-300 rounded-sm"
                                        title={`${importantUnfilled} important field${importantUnfilled === 1 ? "" : "s"} still empty`}
                                        data-testid={`screening-block-important-count-${block.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                    >
                                        {importantUnfilled} to fill
                                    </span>
                                )}
                            </span>
                            <span className="text-xs text-muted-foreground">{list.length} fields</span>
                        </button>
                        {open && (
                            <div className="px-3 sm:px-4 pb-3 pt-1 border-t border-border">
                                {block === "Compensation" ? (
                                    <CompensationBlock
                                        list={list}
                                        profile={profile}
                                        meta={meta}
                                        savingKey={savingKey}
                                        onChange={update}
                                        onChangeMany={updateMany}
                                    />
                                ) : block === "Notice & joining" ? (
                                    // Explicit single-row layout so the three
                                    // Notice questions never wrap onto a second
                                    // row on wide screens (and stack cleanly on
                                    // mobile). Users called out that the 3-col
                                    // grid still felt loose on typical laptop
                                    // widths — this locks the three fields to
                                    // a shared flex row.
                                    <div className="flex flex-wrap sm:flex-nowrap gap-x-4 gap-y-2 items-start" data-testid="sp-notice-joining-row">
                                        {list.map((f) => (
                                            <div key={f.key} className="flex-1 min-w-[180px]">
                                                <FieldRow
                                                    f={f}
                                                    value={profile[f.key]}
                                                    meta={meta?.[f.key]}
                                                    pref={prefs?.[f.key]}
                                                    saving={savingKey === f.key}
                                                    onChange={(v) => update(f.key, v)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    // Compact 2/3-column layout so subsections
                                    // like Notice, Location, Prior Plans, Family
                                    // don't stack vertically and force scroll.
                                    // Textareas span the full row so long-form
                                    // notes stay readable.
                                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-2">
                                        {list.map((f) => (
                                            <div key={f.key} className={f.type === "textarea" ? "sm:col-span-2 lg:col-span-3" : ""}>
                                                <FieldRow
                                                    f={f}
                                                    value={profile[f.key]}
                                                    meta={meta?.[f.key]}
                                                    pref={prefs?.[f.key]}
                                                    saving={savingKey === f.key}
                                                    onChange={(v) => update(f.key, v)}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );
            })}
        </section>
    );
}

function evaluateFieldStatus(value, pref) {
    // Delegate to shared lib — canonical logic in @/lib/screening
    return sharedEvaluate(value, pref);
}

const STATUS_WRAPPER = {
    "empty-important": "border-l-4 border-amber-400 bg-amber-50/60 pl-2 -ml-0.5",
    "preferred":       "border-l-4 border-emerald-500 bg-emerald-50/50 pl-2 -ml-0.5",
    "not-preferred":   "border-l-4 border-rose-500 bg-rose-50/40 pl-2 -ml-0.5",
    "neutral":         "",
};

const STATUS_HINT = {
    "empty-important": { text: "Important — not yet filled", cls: "text-amber-800" },
    "preferred":       { text: "Preferred answer", cls: "text-emerald-700" },
    "not-preferred":   { text: "Not the preferred answer", cls: "text-rose-700" },
    "neutral":         null,
};

function FieldRow({ f, value, meta, pref, saving, onChange }) {
    const id = `sp-${f.key}`;
    const status = evaluateFieldStatus(value, pref);
    const wrapperCls = STATUS_WRAPPER[status] || "";
    const hint = STATUS_HINT[status];
    const labelEl = (
        <Label htmlFor={id} className="text-xs font-medium text-foreground/80 inline-flex items-center gap-2 flex-wrap">
            {f.label}
            {pref?.importance === "important" && (
                <span className="text-[10px] px-1 py-0 bg-amber-100 text-amber-900 border border-amber-300 rounded-sm font-medium" title="Marked as important in your Screening Preferences">important</span>
            )}
            {saving && <span className="text-[10px] text-ikb animate-pulse">saving…</span>}
        </Label>
    );
    const auditEl = meta?.edited_by_name ? (
        <div
            className="text-[11px] text-muted-foreground mt-1"
            title={meta.edited_at ? new Date(meta.edited_at).toString() : undefined}
            data-testid={`sp-meta-${f.key}`}
        >
            Edited by <span className="text-foreground">{meta.edited_by_name}</span>
            {meta.edited_at && <> · {timeAgo(meta.edited_at)}</>}
        </div>
    ) : null;
    const hintEl = hint ? (
        <div className={`text-[11px] mt-1 ${hint.cls}`} data-testid={`sp-hint-${f.key}`}>{hint.text}</div>
    ) : null;
    const wrap = (children) => (
        <div className={wrapperCls} data-testid={`sp-row-${f.key}`} data-status={status}>
            {children}
            {hintEl}
        </div>
    );
    if (f.type === "textarea") {
        return wrap(
            <div>
                {labelEl}
                {/* Compact by default — starts at a single line and expands as
                 *  the content grows. `field-sizing: content` is a modern CSS
                 *  primitive (Chrome/Edge/Safari); Firefox falls back to the
                 *  min-height. Big screening-profile screen no longer scrolls
                 *  for empty sections. */}
                <Textarea
                    id={id}
                    rows={1}
                    defaultValue={value || ""}
                    onBlur={(e) => onChange(e.target.value)}
                    className="rounded-none mt-1 text-sm min-h-[36px] resize-y"
                    style={{ fieldSizing: "content" }}
                    data-testid={`sp-${f.key}`}
                />
                {auditEl}
            </div>
        );
    }
    if (f.type === "number") {
        return wrap(
            <div>
                {labelEl}
                <Input id={id} type="number" defaultValue={value ?? ""} onBlur={(e) => onChange(e.target.value === "" ? null : parseFloat(e.target.value))} className="rounded-none mt-1 h-9 max-w-[10rem]" data-testid={`sp-${f.key}`} />
                {auditEl}
            </div>
        );
    }
    if (f.type === "date") {
        return wrap(
            <div>
                {labelEl}
                <Input id={id} type="date" defaultValue={value || ""} onBlur={(e) => onChange(e.target.value)} className="rounded-none mt-1 h-9 max-w-[16rem]" data-testid={`sp-${f.key}`} />
                {auditEl}
            </div>
        );
    }
    if (f.type === "yesno" || f.type === "select") {
        const opts = f.type === "yesno" ? ["", "Yes", "No", "Not asked"] : (f.options || []);
        return wrap(
            <div>
                {labelEl}
                <select id={id} value={value || ""} onChange={(e) => onChange(e.target.value)} className="mt-1 h-9 px-2 border border-border bg-white text-sm" data-testid={`sp-${f.key}`}>
                    {opts.map((o) => <option key={o} value={o}>{o || "— select —"}</option>)}
                </select>
                {auditEl}
            </div>
        );
    }
    if (f.type === "rating5") {
        const cur = parseInt(value || 0);
        return wrap(
            <div>
                {labelEl}
                <div className="mt-1 inline-flex gap-1" data-testid={`sp-${f.key}`}>
                    {[1, 2, 3, 4, 5].map((n) => (
                        <button
                            key={n}
                            type="button"
                            onClick={() => onChange(n === cur ? 0 : n)}
                            className={`w-7 h-7 inline-flex items-center justify-center border ${n <= cur ? "bg-ikb border-ikb text-white" : "bg-white border-border text-muted-foreground hover:bg-secondary"}`}
                            data-testid={`sp-${f.key}-${n}`}
                            aria-label={`${n} of 5`}
                        >
                            <Star className="w-3.5 h-3.5" fill={n <= cur ? "currentColor" : "none"} />
                        </button>
                    ))}
                </div>
                {auditEl}
            </div>
        );
    }
    if (f.type === "city") {
        return wrap(
            <div>
                {labelEl}
                <div className="mt-1">
                    <CityAutocomplete
                        value={value || ""}
                        onChange={(v) => onChange(v)}
                        placeholder="Start typing a city or address…"
                        dataTestid={`sp-${f.key}`}
                    />
                </div>
                {auditEl}
            </div>
        );
    }
    // text
    return wrap(
        <div>
            {labelEl}
            <Input id={id} type="text" defaultValue={value ?? ""} onBlur={(e) => onChange(e.target.value)} className="rounded-none mt-1 h-9" data-testid={`sp-${f.key}`} />
            {auditEl}
        </div>
    );
}

function timeAgo(iso) {
    try {
        const d = new Date(iso);
        const diff = Math.floor((Date.now() - d.getTime()) / 1000);
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
        return d.toLocaleDateString();
    } catch { return iso; }
}

/**
 * CompensationBlock — custom renderer for the Compensation screening block.
 *
 * Two derivation rules, both bi-directional where it makes sense:
 *   • Annual × Monthly  — typing Annual auto-fills Monthly (annual/12); typing
 *     Monthly auto-fills Annual (monthly*12). Applies to both current AND desired.
 *   • Increment %  — derived from current_ctc_annual and desired_ctc_annual.
 *     READ-ONLY (typing increment would create a 2-degree-of-freedom system).
 *
 * Every keystroke is debounced via the shared saveTimer. When an edit triggers
 * a cascade (e.g. typing Current Annual updates Monthly + Increment), we use
 * onChangeMany() to ship them all in ONE PATCH so the audit log records each
 * field correctly but only one rollup event fires per save burst.
 */
function CompensationBlock({ list, profile, meta, savingKey, onChange, onChangeMany }) {
    const byKey = list.reduce((m, f) => ({ ...m, [f.key]: f }), {});
    const currency = profile.ctc_currency || "";

    const fmt = (n) => {
        if (n === null || n === undefined || n === "" || Number.isNaN(Number(n))) return "";
        return Number(n);
    };
    const num = (v) => {
        const n = parseFloat(v);
        return Number.isNaN(n) ? null : n;
    };

    const onAnnualChange = (annualKey, monthlyKey, otherAnnualKey) => (raw) => {
        const annual = num(raw);
        const patch = { [annualKey]: annual };
        if (annual !== null) patch[monthlyKey] = Math.round((annual / 12) * 100) / 100;
        else patch[monthlyKey] = null;
        // Recompute desired_increment_pct
        const cur = annualKey === "current_ctc_annual" ? annual : num(profile.current_ctc_annual);
        const des = annualKey === "desired_ctc_annual" ? annual : num(profile.desired_ctc_annual);
        if (cur && des && cur > 0) {
            patch.desired_increment_pct = Math.round(((des - cur) / cur) * 1000) / 10;
        } else {
            patch.desired_increment_pct = null;
        }
        // Hint that the other annual side might also have changed (don't write it)
        void otherAnnualKey;
        onChangeMany(patch);
    };

    const onMonthlyChange = (monthlyKey, annualKey) => (raw) => {
        const monthly = num(raw);
        const patch = { [monthlyKey]: monthly };
        const newAnnual = monthly !== null ? Math.round(monthly * 12 * 100) / 100 : null;
        patch[annualKey] = newAnnual;
        const cur = annualKey === "current_ctc_annual" ? newAnnual : num(profile.current_ctc_annual);
        const des = annualKey === "desired_ctc_annual" ? newAnnual : num(profile.desired_ctc_annual);
        if (cur && des && cur > 0) {
            patch.desired_increment_pct = Math.round(((des - cur) / cur) * 1000) / 10;
        } else {
            patch.desired_increment_pct = null;
        }
        onChangeMany(patch);
    };

    const renderAudit = (k) => {
        const m = meta?.[k];
        if (!m?.edited_by_name) return null;
        return (
            <div className="text-[11px] text-muted-foreground mt-1" data-testid={`sp-meta-${k}`}>
                Edited by <span className="text-foreground">{m.edited_by_name}</span>
                {m.edited_at && <> · {timeAgo(m.edited_at)}</>}
            </div>
        );
    };

    const renderLabel = (k) => (
        <Label className="text-xs font-medium text-foreground/80 inline-flex items-center gap-2">
            {byKey[k]?.label || k}{savingKey === k && <span className="text-[10px] text-ikb animate-pulse">saving…</span>}
        </Label>
    );

    return (
        <div className="space-y-4" data-testid="sp-compensation-block">
            {/* Currency */}
            <div>
                {renderLabel("ctc_currency")}
                <select
                    value={currency}
                    onChange={(e) => onChange("ctc_currency", e.target.value)}
                    className="mt-1 h-9 px-2 border border-border bg-white text-sm w-full sm:w-40"
                    data-testid="sp-ctc_currency"
                >
                    {(byKey.ctc_currency?.options || []).map((o) => <option key={o} value={o}>{o || "— select —"}</option>)}
                </select>
                {renderAudit("ctc_currency")}
            </div>

            {/* Current bloc */}
            <div className="border border-border bg-secondary/30 p-3">
                <div className="text-xs font-semibold text-foreground/80 mb-2">Current pay</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                        {renderLabel("current_ctc_annual")}
                        <CurrencyInput
                            currency={currency}
                            value={fmt(profile.current_ctc_annual)}
                            onCommit={onAnnualChange("current_ctc_annual", "current_monthly_gross", "desired_ctc_annual")}
                            testid="sp-current_ctc_annual"
                        />
                        {renderAudit("current_ctc_annual")}
                    </div>
                    <div>
                        {renderLabel("current_monthly_gross")}
                        <CurrencyInput
                            currency={currency}
                            value={fmt(profile.current_monthly_gross)}
                            onCommit={onMonthlyChange("current_monthly_gross", "current_ctc_annual")}
                            testid="sp-current_monthly_gross"
                            helper="= Annual / 12"
                        />
                        {renderAudit("current_monthly_gross")}
                    </div>
                    <div>
                        {renderLabel("previous_monthly_in_hand")}
                        <CurrencyInput
                            currency={currency}
                            value={fmt(profile.previous_monthly_in_hand)}
                            onCommit={(v) => onChange("previous_monthly_in_hand", num(v))}
                            testid="sp-previous_monthly_in_hand"
                        />
                        {renderAudit("previous_monthly_in_hand")}
                    </div>
                </div>
            </div>

            {/* Desired bloc */}
            <div className="border border-border bg-secondary/30 p-3">
                <div className="text-xs font-semibold text-foreground/80 mb-2">Desired pay</div>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                        {renderLabel("desired_ctc_annual")}
                        <CurrencyInput
                            currency={currency}
                            value={fmt(profile.desired_ctc_annual)}
                            onCommit={onAnnualChange("desired_ctc_annual", "desired_monthly_gross", "current_ctc_annual")}
                            testid="sp-desired_ctc_annual"
                        />
                        {renderAudit("desired_ctc_annual")}
                    </div>
                    <div>
                        {renderLabel("desired_monthly_gross")}
                        <CurrencyInput
                            currency={currency}
                            value={fmt(profile.desired_monthly_gross)}
                            onCommit={onMonthlyChange("desired_monthly_gross", "desired_ctc_annual")}
                            testid="sp-desired_monthly_gross"
                            helper="= Annual / 12"
                        />
                        {renderAudit("desired_monthly_gross")}
                    </div>
                    <div>
                        <Label className="text-[11px] uppercase tracking-widest font-mono text-muted-foreground">Desired Increment %</Label>
                        <div className="mt-1 h-9 px-2 border border-border bg-white text-sm flex items-center justify-between" data-testid="sp-desired_increment_pct">
                            <span className={`font-mono font-bold ${profile.desired_increment_pct > 0 ? "text-emerald-700" : profile.desired_increment_pct < 0 ? "text-rose-700" : "text-muted-foreground"}`}>
                                {profile.desired_increment_pct !== undefined && profile.desired_increment_pct !== null
                                    ? `${profile.desired_increment_pct > 0 ? "+" : ""}${profile.desired_increment_pct}%`
                                    : "—"}
                            </span>
                            <span aria-hidden="true" className="text-[9px] font-mono uppercase tracking-widest text-muted-foreground">auto</span>
                        </div>
                        {renderAudit("desired_increment_pct")}
                    </div>
                </div>
            </div>
        </div>
    );
}

/**
 * CurrencyInput — number-only Input prefixed by the chosen currency code.
 * `onCommit` fires on blur (so we don't trigger a network save on every
 * keystroke — the parent's debounce handles bursts cleanly).
 */
function CurrencyInput({ currency, value, onCommit, testid, helper }) {
    const [local, setLocal] = useState(value ?? "");
    useEffect(() => { setLocal(value ?? ""); }, [value]);
    return (
        <div>
            <div className="mt-1 flex items-stretch">
                {currency && (
                    <span className="inline-flex items-center px-2 border border-r-0 border-border bg-secondary text-[10px] font-mono uppercase tracking-widest">
                        {currency}
                    </span>
                )}
                <Input
                    type="number"
                    inputMode="numeric"
                    value={local}
                    onChange={(e) => setLocal(e.target.value)}
                    onBlur={(e) => onCommit(e.target.value)}
                    className="rounded-none h-9 flex-1"
                    data-testid={testid}
                />
            </div>
            {helper && <div className="text-[9px] font-mono text-muted-foreground mt-1">{helper}</div>}
        </div>
    );
}
